/**
 * ILF Web Components v1.1 — shared library of generic UI custom elements.
 *
 * Role (see the ilf-library-section skill for the full ownership rules):
 * - This file owns commerce-agnostic UI primitives, reusable by any ILF
 *   section: <ilf-modal>, <ilf-slider>, <ilf-auto-video>.
 * - Product/cart behaviour lives in assets/ilf-commerce.js, which DEPENDS on
 *   this file (<ilf-quick-view> opens an <ilf-modal>). Every component has
 *   exactly one home — never define the same element in both files.
 * - Deployed byte-identical to every ILF store. This store loads it
 *   site-wide via theme.liquid; sections that need it must still load it
 *   themselves (<script src defer>) — all registrations are guarded, so
 *   loading the file more than once is safe.
 *
 * Legacy note: <ilf-upsell> here predates the commerce runtime and runs its
 * own add-to-cart pipeline. New work uses IlfCart (ilf-commerce.js); this
 * element migrates there when its consumer sections get rebuilt.
 */

if (!customElements.get("ilf-modal")) {
    class IlfModal extends HTMLElement {
        constructor() {
            super()
            this.isOpen = false
        }

        connectedCallback() {
            // Move modal to body for better z-index and positioning
            if (document.body && this.parentElement !== document.body) {
                // appendChild re-invokes connectedCallback from the new position
                document.body.appendChild(this)
                return
            }
            if (this.initialised) return

            const modal = this.querySelector(".ilf-modal-content-wrapper")
            const close = this.querySelector(".ilf-modal-close")
            if (!modal || !close) return
            this.initialised = true

            // Store references for cleanup
            this.close = close
            this.modal = modal

            // The trigger is optional: modals opened programmatically (e.g. by
            // <ilf-quick-view> in ilf-commerce.js) have no trigger of their own
            const btn = document.querySelector(`[data-ilf-modal-open="${this.id}"]`)
            if (btn) {
                this.btn = btn
                btn.addEventListener("click", this.openModal.bind(this))
            }

            close.addEventListener("click", this.closeModal.bind(this))
            this.addEventListener("click", this.handleBackdropClick.bind(this))

            // Add keyboard support
            this.addEventListener("keydown", this.handleKeydown.bind(this))
        }

        // Accepts either the trigger's click event or, when called
        // programmatically, the element focus should return to on close
        openModal(eventOrOpener) {
            if (eventOrOpener instanceof HTMLElement) this.btn = eventOrOpener
            // *** ILF Custom: state via classes (ilf-modal.css .is-open / body.ilf-modal-open)
            this.classList.add("is-open")
            this.isOpen = true
            // Prevent body scroll when modal is open
            document.body.classList.add("ilf-modal-open")
            this.close.focus()
        }

        closeModal() {
            this.classList.remove("is-open")
            this.isOpen = false
            // Restore body scroll when modal is closed
            document.body.classList.remove("ilf-modal-open")
            if (this.btn) {
                this.btn.focus()
            }
        }

        handleBackdropClick(event) {
            if (event.target === this) {
                this.closeModal()
            }
        }

        handleKeydown(event) {
            if (!this.isOpen) return
            if (event.key === "Escape") {
                this.closeModal()
            }
            if (event.key === "Tab") {
                this.maintainFocus(event)
            }
        }

        // *** ILF Custom: focus trap — Tab cycles inside the open modal
        maintainFocus(event) {
            const focusable = Array.from(
                this.querySelectorAll(
                    "a[href], button:not([disabled]), input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^='-'])"
                )
            ).filter((el) => el.offsetParent !== null)
            if (!focusable.length) return

            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        disconnectedCallback() {
            // Clean up body scroll when modal is removed
            if (this.isOpen) {
                document.body.classList.remove("ilf-modal-open")
            }
        }
    }

    customElements.define("ilf-modal", IlfModal)
}

/**
 * ILF UPSELL COMPONENT
 * Handles add-to-cart logic, inventory validation, and cart drawer updates.
 */
if (!customElements.get("ilf-upsell")) {
  class IlfUpsell extends HTMLElement {
    constructor() {
      super();
      
      // 1. Initialize State from Data Attributes
      this.variantId = this.dataset.variantId;
      this.maxStock = parseInt(this.dataset.maxStock) || 9999;
      this.isAvailable = this.dataset.available === "true";
      this.basePrice = parseInt(this.dataset.price) || 0;
      
      // 2. Select Internal Elements
      this.submitBtn = this.querySelector(".ilf-upsell-submit-btn");
      this.spinner = this.querySelector(".loading__spinner");
      this.buttonText = this.querySelector(".button-text");
      this.messageContainer = this.querySelector(".ilf-btn-message");
      
      this.qtyInput = this.querySelector(".ilf-qty-input");
      this.plusBtn = this.querySelector('[data-action="plus"]');
      this.minusBtn = this.querySelector('[data-action="minus"]');
      this.errorContainer = this.querySelector(".ilf-error-message");
      this.priceElement = this.querySelector(".price-value"); // Price display element
    }

    connectedCallback() {
      // If sold out or missing button, do not initialize listeners
      if (!this.isAvailable || !this.submitBtn) return;
      
      this.initEventListeners();
      this.updatePriceDisplay();
    }

    initEventListeners() {
      // Add to Cart Action
      this.submitBtn.addEventListener("click", () => {
        this.handleAddToCart();
      });

      // Quantity Logic (Only if the input exists in the DOM)
      if (this.qtyInput) {
        if (this.plusBtn) {
          this.plusBtn.addEventListener("click", () => this.updateQuantity(1));
        }
        if (this.minusBtn) {
          this.minusBtn.addEventListener("click", () => this.updateQuantity(-1));
        }
        
        // Manual typing validation
        this.qtyInput.addEventListener("change", () => this.validateQuantity());
        this.qtyInput.addEventListener("input", () => this.validateQuantity());
      }
    }

    // ==========================================
    // LOGIC: Quantity Management
    // ==========================================

    updateQuantity(change) {
      if (!this.qtyInput) return;
      
      let currentQty = parseInt(this.qtyInput.value) || 0;
      let newQty = currentQty + change;
      
      this.qtyInput.value = newQty;
      this.validateQuantity();
    }

    updatePriceDisplay() {
      if (!this.priceElement) return;
      
      const quantity = parseInt(this.qtyInput.value) || 1;
      const totalCents = this.basePrice * quantity;
      
      // Simple formatting: cents -> decimal with 2 places
      const total = (totalCents / 100).toFixed(2);
      this.priceElement.textContent = total;
    }

    validateQuantity() {
      if (!this.qtyInput) return;

      let value = parseInt(this.qtyInput.value);
      const min = 1;

      const effectiveValue = isNaN(value) ? min : value;

      // 1. Enforce Minimum
      if (effectiveValue < min) {
        this.qtyInput.value = min;
        value = min; // update local var for max check
      }

      // 2. Enforce Maximum (Stock Check)
      // Use effectiveValue to ensure we don't block empty inputs unnecessarily
      if (effectiveValue > this.maxStock) {
        // Gray out button & Show Message
        this.submitBtn.classList.add("unavailable", "has-message");
        this.submitBtn.setAttribute("disabled", "disabled");
        
        if (this.messageContainer) {
            this.messageContainer.textContent = `Max Available: ${this.maxStock}`;
            this.messageContainer.classList.remove('hidden');
        }
      } else {
        // Restore button & Hide Message
        this.submitBtn.classList.remove("unavailable", "has-message");
        this.submitBtn.removeAttribute("disabled");
        
        if (this.messageContainer) {
            this.messageContainer.classList.add('hidden');
            this.messageContainer.textContent = '';
        }
        
        this.updatePriceDisplay();
      }
    }

    // ==========================================
    // LOGIC: Add To Cart (AJAX)
    // ==========================================

    toggleLoading(isLoading) {
        if (isLoading) {
            this.submitBtn.classList.add('is-loading');
        } else {
            this.submitBtn.classList.remove('is-loading');
            this.validateQuantity();
        }
    }

    handleAddToCart() {
      this.hideErrorMessage();
      
      // Default to 1 if quantity input is missing or invalid/empty
      let quantity = 1;
      if (this.qtyInput) {
        const parsed = parseInt(this.qtyInput.value);
        if (!isNaN(parsed) && parsed > 0) {
            quantity = parsed;
        }
      }

      const formData = {
        items: [{
          id: this.variantId,
          quantity: quantity
        }],
        sections: "cart-drawer,cart-icon-bubble"
      };

      this.toggleLoading(true);

      this.addProductToCart(formData);
    }

    addProductToCart(formData) {
      fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      })
      .then(response => {
        return response.json();
      })
      .then(data => {
        this.toggleLoading(false);

        // Handle Errors (e.g. 422 Unprocessable Entity - Sold Out)
        if (data.status === 422) {
          this.showErrorMessage(data.description);
          return;
        }

        // Success: Update UI Sections
        if (data.sections) {
          this.updateCartSections(data.sections);
        }
        
        // Optional: Open Cart Drawer if method exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.open) cartDrawer.open();
      })
      .catch(error => {
        console.error("Error:", error);
        this.toggleLoading(false);
        this.showErrorMessage("Error adding to cart");
      });
    }

    // ==========================================
    // LOGIC: DOM Parsing & Updates
    // ==========================================

    // Helper: Extract inner HTML of a specific section
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
    }

    updateCartSections(sections) {
      // 1. Update Cart Drawer
      const cartDrawerHTML = sections["cart-drawer"];
      if (cartDrawerHTML) {
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) {
          // Dawn typically wraps content in class .cart-drawer
          const newCartDrawerContent = this.getSectionInnerHTML(cartDrawerHTML, ".cart-drawer") || this.getSectionInnerHTML(cartDrawerHTML, "cart-drawer");
          
          if (newCartDrawerContent) {
            cartDrawer.classList.remove("is-empty");
            
            // Depending on theme version, replace the inner container
            const drawerInner = document.getElementById("CartDrawer");
            if (drawerInner) {
              drawerInner.innerHTML = this.getSectionInnerHTML(cartDrawerHTML, "#CartDrawer");
            } else {
               // Fallback if ID not found
               cartDrawer.innerHTML = newCartDrawerContent;
            }
          }
        }
      }

      // 2. Update Cart Icon
      const cartIconHTML = sections["cart-icon-bubble"];
      if (cartIconHTML) {
        const newCartIconContent = this.getSectionInnerHTML(cartIconHTML, "#shopify-section-cart-icon-bubble");
        const iconContainer = document.querySelector("#cart-icon-bubble");
        if (iconContainer && newCartIconContent) {
          iconContainer.innerHTML = newCartIconContent;
        }
      }
    }

    // ==========================================
    // LOGIC: Error Handling
    // ==========================================

    showErrorMessage(msg) {
      if (this.errorContainer) {
        this.errorContainer.innerText = msg;
        this.errorContainer.classList.remove("hidden");
      } else {
        alert(msg); // Fallback if container missing
      }
    }

    hideErrorMessage() {
      if (this.errorContainer) {
        this.errorContainer.classList.add("hidden");
      }
    }
  }

  // Register Component
  customElements.define("ilf-upsell", IlfUpsell);
}

/**
 * IlfSlider
 * A generic wrapper for Swiper.js
 * Expects Swiper HTML structure (swiper, swiper-wrapper, swiper-slide) as children.
 *
 * Usage:
 * <ilf-slider data-loop="true" data-autoplay="3000" data-effect="slide">
 *   ... swiper html ...
 * </ilf-slider>
 */

if (!customElements.get("ilf-slider")) {
  class IlfSlider extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.initTimer = null;
    }

    connectedCallback() {
      // Small timeout ensures DOM children are fully parsed by the browser
      requestAnimationFrame(() => this.initSwiper());
    }

    disconnectedCallback() {
      if (this.initTimer) {
        clearTimeout(this.initTimer);
        this.initTimer = null;
      }
      if (this.swiperInstance) {
        this.swiperInstance.destroy();
        this.swiperInstance = null;
      }
    }

    /**
     * Parse data attributes to build Swiper config
     */
    getOptions() {
      const loop = this.dataset.loop === "true";
      const autoplayDelay = parseInt(this.dataset.autoplay, 10);
      const effect = this.dataset.effect || "slide"; // slide, fade, cube, etc.
      const slidesPerView = this.dataset.slidesPerView ? parseFloat(this.dataset.slidesPerView) : 1;
      const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 0;

      // Basic Config
      const config = {
        loop: loop,
        effect: effect,
        slidesPerView: slidesPerView,
        spaceBetween: spaceBetween,
        grabCursor: true,
        threshold: 10, // Touch sensitivity
        navigation: {},
        pagination: {},
      };

      // Handle Autoplay
      if (!isNaN(autoplayDelay) && autoplayDelay > 0) {
        config.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
        };
      }

      // Handle Navigation (Scoped to this element)
      const nextEl = this.querySelector(".swiper-button-next");
      const prevEl = this.querySelector(".swiper-button-prev");

      if (nextEl && prevEl) {
        config.navigation = {
          nextEl: nextEl,
          prevEl: prevEl,
        };
      }

      // Handle Pagination (Optional, if markup exists)
      const paginationEl = this.querySelector(".swiper-pagination");
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }

      return config;
    }

    initSwiper(attempts = 0, delay = 100) {
      this.initTimer = null;

      // Prevent double init
      if (this.swiperInstance) return;

      const swiperContainer = this.querySelector(".swiper");
      
      if (!swiperContainer) {
        console.warn("IlfSlider: No element with class .swiper found inside <ilf-slider>");
        return;
      }

      if (!window.Swiper) {
        const MAX_ATTEMPTS = 10;
        
        if (attempts >= MAX_ATTEMPTS) {
          console.error(`IlfSlider: Swiper.js failed to load after ${MAX_ATTEMPTS} attempts.`);
          return;
        }
        
        const nextDelay = Math.round(delay * 1.5);
        this.initTimer = setTimeout(() => this.initSwiper(attempts + 1, nextDelay), delay);
        return;
      }

      const config = this.getOptions();
      this.swiperInstance = new window.Swiper(swiperContainer, config);
    }
  }

  customElements.define("ilf-slider", IlfSlider);
}

/**
 * ILF AUTO VIDEO COMPONENT
 * Wraps a <video> element and automatically plays it when visible,
 * pauses it when hidden. Visibility is detected via IntersectionObserver,
 * which covers display: none, visibility: hidden, off-screen scroll, and
 * zero-size containers - all collapse into a single "is showing" signal.
 *
 * Designed for cases where the same video is rendered in multiple
 * containers (e.g. mobile vs desktop layouts) and only the visible one
 * should consume bandwidth, CPU, and battery.
 *
 * Key behavior:
 * - preload="none" by default, so hidden videos never download
 * - muted + playsInline + loop enforced for reliable autoplay
 * - Pauses automatically when scrolled off screen
 * - Reflects playback through the "is-playing" class on the element
 *   (added on the video's "playing" event, removed on "pause" and "ended"),
 *   so styles can reveal the video over a poster only once frames render.
 *   A video that already plays when the element connects (native autoplay
 *   attribute, script executed late) is picked up immediately.
 * - Dispatches document CustomEvents ilf:auto-video:play and
 *   ilf:auto-video:pause (detail: { element, video })
 *
 * Attributes:
 * - respect-motion-preference: when present, users with
 *   prefers-reduced-motion: reduce or Save-Data enabled never load or play
 *   the video, so whatever sits behind it (a poster) stays visible.
 *
 * Usage:
 * <ilf-auto-video>
 *   <video src="..." loop muted playsinline preload="none"></video>
 * </ilf-auto-video>
 */
if (!customElements.get("ilf-auto-video")) {
  class IlfAutoVideo extends HTMLElement {
    constructor() {
      super();
      this.video = null;
      this.observer = null;
    }

    connectedCallback() {
      // Re-entry guard: a second connect without a disconnect must not stack observers
      if (this.observer) return;

      // 1. Locate the inner <video> element
      this.video = this.querySelector("video");
      if (!this.video) {
        console.warn("IlfAutoVideo: No <video> element found inside <ilf-auto-video>");
        return;
      }

      // 2. Opt-in: motion-sensitive and data-saving users keep the poster
      if (this.hasAttribute("respect-motion-preference") && IlfAutoVideo.prefersStillMedia()) {
        return;
      }

      // 3. Enforce attributes required for reliable autoplay
      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.preload = "none";

      // 4. Mirror playback state onto the element
      this.onPlaying = this.onPlaying || (() => this.setPlaying(true));
      this.onPause = this.onPause || (() => this.setPlaying(false));
      this.video.addEventListener("playing", this.onPlaying);
      this.video.addEventListener("pause", this.onPause);
      this.video.addEventListener("ended", this.onPause);
      if (!this.video.paused && this.video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        this.setPlaying(true);
      }

      // 5. Observe self for visibility changes
      this.observer = new IntersectionObserver(
        ([entry]) => this.handleVisibility(entry.isIntersecting),
        { threshold: 0.01 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      // Clean up observer, listeners and pause video to release resources
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.video) {
        this.video.removeEventListener("playing", this.onPlaying);
        this.video.removeEventListener("pause", this.onPause);
        this.video.removeEventListener("ended", this.onPause);
        this.video.pause();
      }
    }

    // ==========================================
    // SERVICE: User preferences
    // ==========================================

    static prefersStillMedia() {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const saveData = Boolean(navigator.connection && navigator.connection.saveData);
      return reducedMotion || saveData;
    }

    // ==========================================
    // LOGIC: Visibility Handling
    // ==========================================

    handleVisibility(isVisible) {
      if (!this.video) return;

      if (isVisible) {
        // Upgrade preload on first activation so the file starts loading
        if (this.video.preload === "none") {
          this.video.preload = "auto";
        }
        // play() returns a promise that may reject (e.g. autoplay policy);
        // swallow it silently to avoid console noise
        this.video.play().catch(() => {});
      } else {
        this.video.pause();
      }
    }

    // ==========================================
    // LOGIC: Playback state
    // ==========================================

    setPlaying(isPlaying) {
      this.classList.toggle("is-playing", isPlaying);
      document.dispatchEvent(
        new CustomEvent(isPlaying ? "ilf:auto-video:play" : "ilf:auto-video:pause", {
          detail: { element: this, video: this.video },
        })
      );
    }
  }

  // Register Component
  customElements.define("ilf-auto-video", IlfAutoVideo);
}
